export type Platform = 'ebay' | 'poshmark' | 'mercari' | 'unknown';
export type SaleType = 'fixed' | 'auction' | 'auction_bin';
export type ExcludeReason = 'smart' | 'casio_cheap' | 'accessory';
export type FetchHealth = 'ok' | 'degraded' | 'down';
export type ShowMode = 'unseen' | 'all' | 'saved';
export type BrandKind = 'horology' | 'fashion' | 'microbrand' | 'smart' | 'casio_family';

export const EXCLUDE_REASONS: ExcludeReason[] = ['smart', 'casio_cheap', 'accessory'];

/** internal/core/filter_result.go MatchTrace — json tags, omitempty fields optional. */
export interface MatchTrace {
  tier: number;
  tier_reason: string;
  matched_brand?: string;
  matched_model?: string;
  matched_refs?: string[];
  matched_keywords?: string[];
  exclude_reason?: string;
  exclude_detail?: string;
}

/** internal/api/dto.go apiListing — every key present; nullable ⇒ `| null`. */
export interface Listing {
  id: number;
  platform: Platform;
  external_id: string;
  url: string;
  title: string;
  price_usd: number | null;
  currency: string;
  sale_type: SaleType;
  auction_ends_at: string | null;
  seller_name: string | null;
  seller_feedback_pct: number | null;
  item_location: string | null;
  listed_at: string;
  primary_image_url: string | null;
  image_urls: string[];            // toAPIListing fills [] when nil
  category_id: string | null;
  item_specifics: Record<string, string[]>; // toAPIListing fills {} when nil
  tier: number;
  exclude_reason: ExcludeReason | null;
  match_trace: MatchTrace | null;  // toAPIListing emits null when empty
  first_seen_at: string;
  last_seen_at: string;
  removed_at: string | null;
  seen_at: string | null;
  saved_at: string | null;
  hidden_at: string | null;
  note: string | null;
}

export interface FeedResponse {
  items: Listing[];
  next_cursor: string; // opaque base64; '' ⇒ no more pages
}

/** internal/health/health.go Snapshot — một per-platform. */
export interface PlatformHealth {
  last_fetch_at: string | null;
  fetch_health: FetchHealth;
  fetch_health_detail?: string;
}

/** internal/api/dto.go statsResponse. */
export interface Stats {
  new_since_last_visit: number;
  counts_by_tier: Record<string, number>;    // keys "1" "2" "3"
  counts_by_exclude: Record<string, number>; // keys smart casio_cheap accessory
  ebay_health: PlatformHealth;
  poshmark_health: PlatformHealth;
  mercari_health: PlatformHealth;
}

/** internal/api/dto.go apiBrand. */
export interface Brand {
  id: number;
  canonical_name: string;
  aliases: string[];
  kind: BrandKind;
  requires_watch_context: boolean;
  active: boolean;
  source: string[];
  notes: string | null;
  updated_by: string | null;
  updated_at: string;
}

/** POST /api/listing/{id}/state body — all optional, at least one required. */
export interface StatePatch {
  seen?: boolean;
  saved?: boolean;
  hidden?: boolean;
  note?: string;
}

/** Thrown by the API client for any non-2xx (and 401 after notifying the session store). */
export class ApiErr extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiErr';
    this.status = status;
    this.code = code;
  }
}
